import { api } from "../../lib/api";
import { getGoogleMapsApiKey } from "../../lib/getEnvVariables";
export const getProjectListData = async (queryParams) => {
    const { data } = await api.post(`/projects/search${queryParams && "?" + queryParams}`);
    return data;
};
export const getProjectAssetsData = async (id, queryParams) => {
    const { data } = await api.post(`/projects/${id}/assets/search${queryParams && "?" + queryParams}`);
    return data;
};
export const getUsageHistoryDetailsData = async (id, startDate, aggregation, assetSourceType, assetId) => {
    const config = { params: { aggregation, assetSourceType, startDate, ...(assetId && { assetId }) } };
    const { data } = await api.get(`/projects/${id}/energy/histogram`, config);
    return data;
};
/**
 * Fetches productivity aggregated data (material moved, energy used, load count,
 * idle time and cycle-time activities) for a project from the shared energy
 * histogram endpoint using `aggregation=productivity`.
 *
 * @param id - Project id.
 * @param startDate - Selected date (yyyy-MM-dd).
 * @param duration - Aggregation window (hourly | daily | weekly | monthly | daywiseMonth).
 * @param assetSourceType - Asset source type (MACHINE | ESS).
 * @param assetId - Mandatory only when `duration` is `daywiseMonth`.
 */
export const getProjectProductivityData = async (id, startDate, duration, assetSourceType, assetId) => {
    if (duration === "daywiseMonth" && !assetId) {
        throw new Error('assetId is required when duration is "daywiseMonth"');
    }
    const config = {
        headers: { "x-api-version": "2.0" },
        params: {
            aggregation: "productivity",
            assetSourceType,
            duration,
            startDate,
            ...(assetId && { assetId })
        }
    };
    const { data } = await api.get(`/projects/${id}/energy/histogram`, config);
    return data;
};
export const getProjectEnergyMetricsData = async (id, startDate) => {
    const config = { params: { startDate } };
    const { data } = await api.get(`/projects/${id}/energy/metrics`, config);
    return data;
};
export const getProjectNetworkData = async (id) => {
    const { data } = await api.get(`/projects/${id}/network/connections`);
    return data;
};
export const getProjectEnergyActivitiesData = async (id, startDate) => {
    const config = { params: { startDate } };
    const { data } = await api.get(`/projects/${id}/energy/activities`, config);
    return data;
};
export const getProjectAssetChargeActivitiesData = async (id, startDate) => {
    const { data } = await api.post(`/projects/${id}/assets/events/search?startDate=${startDate}`);
    return data;
};
export const getProjectAssetChargeActivitiesDataWithAssetId = async (id, startDate, assetId) => {
    const requestParams = {
        filters: [
            {
                propertyName: "assetId",
                type: "stringEquals",
                values: [assetId]
            }
        ]
    };
    const { data } = await api.post(`/projects/${id}/assets/events/search?startDate=${startDate}`, requestParams);
    return data;
};
export const getMapDirectionData = async (routeSources) => {
    const config = {
        headers: {
            "X-Goog-Api-Key": getGoogleMapsApiKey(),
            "X-Goog-FieldMask": "routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline,routes.legs"
        }
    };
    const routes = await Promise.all(routeSources.map(async (routeSource) => {
        const requestBody = JSON.stringify({
            destination: routeSource.destination,
            origin: routeSource.origin,
            travelMode: "DRIVE"
        });
        const { data } = await api.post(`https://routes.googleapis.com/directions/v2:computeRoutes`, requestBody, config);
        return {
            destAssetName: routeSource.destAssetName,
            id: routeSource.id,
            routeData: data.routes[0],
            srcAssetName: routeSource.srcAssetName,
            srcAssetNameFull: routeSource.srcAssetNameFull,
            srcAssetNameToOthers: routeSource.srcAssetNameToOthers
        };
    }));
    return { routes };
};
export const getAssetSocChartDetailsData = async (assetId) => {
    const { data } = await api.post(`/assets/${assetId}/idle/burndown`);
    return data;
};
export const getMethanolAssetEvents = async (projectId, assetId, fromDate, toDate, queryParams) => {
    const params = new URLSearchParams();
    if (queryParams?.startDate)
        params.set("startDate", queryParams.startDate);
    if (queryParams?.limit)
        params.set("limit", queryParams.limit);
    if (queryParams?.skip)
        params.set("skip", queryParams.skip);
    if (queryParams?.cursor)
        params.set("cursor", queryParams.cursor);
    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : "";
    const url = `/projects/${projectId}/assets/events/search${suffix}`;
    const requestBody = {
        filters: [
            {
                propertyName: "assetId",
                type: "stringEquals",
                values: [assetId]
            },
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: true,
                    left: fromDate,
                    right: toDate
                },
                type: "timeRange"
            }
        ]
    };
    const { data } = await api.post(url, requestBody, {
        headers: { "x-api-version": "2.0" }
    });
    return data;
};
export const getAssetTrendsDetailsV2 = async (projectId, assetId, fromDate, toDate, frequency, cursor) => {
    const requestBody = {
        filters: [
            {
                propertyName: "assetId",
                type: "stringEquals",
                values: [assetId]
            },
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: true,
                    left: fromDate,
                    right: toDate
                },
                type: "timeRange"
            }
        ],
        responseAttributes: ["projectId", "projectName", "timezone", "offset", "assetId", "timestamp", "soc", "power"]
    };
    const url = `/projects/${projectId}/assets/trends${frequency !== undefined ? `?frequency=${frequency}` : ""}${cursor ? `${frequency !== undefined ? "&" : "?"}cursor=${cursor}` : ""}`;
    const { data } = await api.post(url, requestBody, {
        headers: {
            "X-API-Version": "2.0"
        }
    });
    return data;
};
// The events/search endpoint pages its results (see the cursor-driven "Show More" pattern used
// elsewhere in this codebase, e.g. MethanolReformerDrawer/UnknownMachineHistoricalInfo), so a
// single request can silently omit data. The chart needs the complete range up front, so this
// follows every cursor until the server reports none are left.
// The maximum number of events to request per page when fetching the complete ESS activity range.
const ESS_RANGE_PAGE_LIMIT = "100";
export const getEssAssetEventsRange = async (projectId, assetId, fromDate, toDate) => {
    const activities = [];
    const seenCursors = new Set();
    let response;
    let cursor;
    do {
        response = await getMethanolAssetEvents(projectId, assetId, fromDate, toDate, {
            cursor,
            limit: ESS_RANGE_PAGE_LIMIT
        });
        activities.push(...(response.assetChargeActivities ?? []));
        cursor = response.responseMetadata?.nextCursor?.trim() || undefined;
        if (cursor && seenCursors.has(cursor)) {
            throw new Error("ESS activity range pagination returned a repeated cursor");
        }
        if (cursor)
            seenCursors.add(cursor);
    } while (cursor);
    return { ...response, assetChargeActivities: activities, responseMetadata: {} };
};
// assets/trends is cursor-paginated. Aggregate every page into a single
// response so chart consumers can treat the selected date range as a
// complete dataset.
export const getAssetTrendsDetailsV2Range = async (projectId, assetId, fromDate, toDate, frequency) => {
    const seenCursors = new Set();
    let response;
    let cursor;
    const aggregatedAssetTrends = [];
    do {
        response = await getAssetTrendsDetailsV2(projectId, assetId, fromDate, toDate, frequency, cursor);
        response.assetTrendsDetails?.forEach((assetTrend) => {
            const existing = aggregatedAssetTrends.find((item) => item.assetId === assetTrend.assetId);
            if (existing) {
                existing.trends = [...(existing.trends ?? []), ...(assetTrend.trends ?? [])];
            }
            else {
                aggregatedAssetTrends.push({
                    ...assetTrend,
                    trends: [...(assetTrend.trends ?? [])]
                });
            }
        });
        cursor = response.responseMetadata?.nextCursor?.trim() || undefined;
        if (cursor) {
            // Defensive guard against pagination loops caused by a repeated cursor from
            // the API. Prevents infinite requests while aggregating the range.
            if (seenCursors.has(cursor)) {
                throw new Error("Asset trends pagination returned a repeated cursor");
            }
            seenCursors.add(cursor);
        }
    } while (cursor);
    return {
        ...response,
        assetTrendsDetails: aggregatedAssetTrends,
        responseMetadata: {}
    };
};
